import {useEffect, useMemo, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {createCheckout, getPackages} from '../../common/api/generated/endpoints/credits/credits'
import type {CreditPackagesResponse} from '../../common/api/generated/model/creditPackagesResponse'
import {useAuth} from '../auth/useAuth'
import {addBreadcrumb} from '../../common/errors/breadcrumb'
import {handleApiError} from '../../common/errors/handleApiError'
import {trackEvent} from '../../common/analytics/trackEvent'
import Icon from '../components/pricing/Icon'
import CheckoutConsentModal from '../components/pricing/CheckoutConsentModal'
import './PricingPage.css'

// Version of the consent statement the user ticks in the checkout modal. Must match a key
// in `be/src/config/purchaseConsent.ts#PURCHASE_CONSENT_TEXTS`. Bump in lockstep with the BE
// whenever the consent copy changes — the BE rejects unknown versions at `/credits/checkout`.
const PURCHASE_CONSENT_VERSION = '2026-05-22-v1'

/** Per-slug front-end copy that joins API package data into the design. */
const PACK_COPY: Record<string, {nameKey: string; taglineKey: string; bestForKey: string; featured?: boolean}> = {
  small: {
    nameKey: 'pricing.packs.small.name',
    taglineKey: 'pricing.packs.small.tagline',
    bestForKey: 'pricing.packs.small.bestFor',
  },
  medium: {
    nameKey: 'pricing.packs.medium.name',
    taglineKey: 'pricing.packs.medium.tagline',
    bestForKey: 'pricing.packs.medium.bestFor',
    featured: true,
  },
  large: {
    nameKey: 'pricing.packs.large.name',
    taglineKey: 'pricing.packs.large.tagline',
    bestForKey: 'pricing.packs.large.bestFor',
  },
}

const INCLUDED_KEYS = [
  'pricing.included.editor',
  'pricing.included.share',
  'pricing.included.earn',
  'pricing.included.devices',
  'pricing.included.resolution',
  'pricing.included.redownload',
  'pricing.included.expire',
  'pricing.included.refund',
] as const

const FAQ_KEYS = ['noSub', 'how', 'expire', 'free', 'refund', 'team'] as const

const formatDollars = (priceCents: number) => {
  const dollars = priceCents / 100
  return Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2)
}

const formatPerCredit = (priceCents: number, credits: number) => {
  const perCredit = priceCents / credits / 100
  return perCredit.toFixed(3)
}

const PricingPage = () => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const {user} = useAuth()

  const [packages, setPackages] = useState<CreditPackagesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingSlug, setLoadingSlug] = useState<string | null>(null)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  const [openFaq, setOpenFaq] = useState<number>(0)
  // Slug of the pack the user is consenting to in the modal (null = modal closed).
  // Loading transitions into `loadingSlug` once the user clicks Continue.
  const [consentSlug, setConsentSlug] = useState<string | null>(null)
  const [consentErrorKey, setConsentErrorKey] = useState<string | null>(null)

  useEffect(() => {
    trackEvent('pricing_page_viewed')
  }, [])

  // After redirecting to Lemon Squeezy the browser may restore this page from the bfcache
  // (back-forward cache) with React state intact — `loadingSlug` would still be set, leaving
  // the consent modal stuck in its disabled "redirecting" state with no way to close it.
  // Clear the redirect state on bfcache restore so the modal becomes interactive again.
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setLoadingSlug(null)
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  useEffect(() => {
    let cancelled = false
    getPackages()
      .then(({data}) => {
        if (!cancelled) setPackages(data)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Step 1: clicking a pack only opens the consent modal — never calls LS directly.
  // The actual checkout call lives in `handleConsentConfirm` and only fires after
  // the user actively ticks the consent box and clicks Continue.
  const handleBuy = (slug: string) => {
    trackEvent('credit_package_clicked', {package_slug: slug, signed_in: user !== null})
    if (!user) {
      navigate('/login')
      return
    }
    setCheckoutError(null)
    setConsentErrorKey(null)
    setConsentSlug(slug)
    trackEvent('pricing_consent_modal_shown', {package_slug: slug})
  }

  const handleConsentClose = () => {
    setConsentSlug(null)
    setConsentErrorKey(null)
  }

  // Step 2: user has ticked the box and confirmed. POST /credits/checkout with
  // the consent version; on success redirect to LS. Errors stay inside the modal
  // so the consent context is preserved.
  const handleConsentConfirm = async () => {
    if (!consentSlug) return
    setLoadingSlug(consentSlug)
    setConsentErrorKey(null)
    addBreadcrumb('payment', 'checkout-start', 'info', {slug: consentSlug})
    trackEvent('pricing_consent_accepted', {package_slug: consentSlug})
    try {
      const {data} = await createCheckout({
        packageSlug: consentSlug,
        consentVersion: PURCHASE_CONSENT_VERSION,
      })
      addBreadcrumb('payment', 'checkout-url-received', 'info', {slug: consentSlug})
      trackEvent('checkout_redirected', {package_slug: consentSlug})
      window.location.assign(data.url)
    } catch (err) {
      handleApiError(err, 'payment-checkout')
      setConsentErrorKey('pricing.consentModal.errorGeneric')
      setLoadingSlug(null)
      const status = (err as {status?: number}).status
      trackEvent('checkout_failed', {
        package_slug: consentSlug,
        error_code: typeof status === 'number' ? String(status) : 'unknown',
      })
    }
  }

  const consentPack = consentSlug ? (packages?.packages.find((p) => p.slug === consentSlug) ?? null) : null
  const consentCopy = consentSlug ? PACK_COPY[consentSlug] : null
  const consentPriceLabel = consentPack ? `$${formatDollars(consentPack.priceCents)}` : ''

  const faqItems = useMemo(
    () =>
      FAQ_KEYS.map((key) => ({
        q: t(`pricing.faq.${key}.q`),
        a: t(`pricing.faq.${key}.a`),
      })),
    [t],
  )

  return (
    <div className="pricing-root">
      <div className="bg-grid" />
      <div className="noise" />

      <main className="pricing-page">
        <header className="pricing-head">
          <div className="pricing-eyebrow">
            <span className="pr-dot" />
            {t('pricing.eyebrow')}
          </div>
          <h1 className="pricing-title">
            {t('pricing.titlePrefix')} <em>{t('pricing.titleEm')}</em>
            {t('pricing.titleSuffix')}
          </h1>
          <p className="pricing-sub">{t('pricing.subtitle')}</p>

          <div className="promise-row">
            <div className="promise">
              <span className="promise-icon">
                <Icon name="no-sub" size={18} />
              </span>
              <div>
                <strong>{t('pricing.promise.noSub.title')}</strong>
                <span>{t('pricing.promise.noSub.body')}</span>
              </div>
            </div>
            <div className="promise">
              <span className="promise-icon">
                <Icon name="infinity" size={18} />
              </span>
              <div>
                <strong>{t('pricing.promise.expire.title')}</strong>
                <span>{t('pricing.promise.expire.body')}</span>
              </div>
            </div>
            <div className="promise">
              <span className="promise-icon">
                <Icon name="shield" size={18} />
              </span>
              <div>
                <strong>{t('pricing.promise.refund.title')}</strong>
                <span>{t('pricing.promise.refund.body')}</span>
              </div>
            </div>
          </div>
        </header>

        {loading && <div className="pricing-empty">{t('pricing.loading')}</div>}

        {!loading && packages && (
          <section className="packs-grid">
            {packages.packages.map((pack) => {
              const copy = PACK_COPY[pack.slug]
              if (!copy) return null
              const ctaLoading = loadingSlug === pack.slug
              const ctaDisabled = loadingSlug !== null
              return (
                <div key={pack.slug} className={`pack ${copy.featured ? 'pack-featured' : ''}`}>
                  {copy.featured && <div className="pack-ribbon">{t('pricing.mostPopular')}</div>}
                  <div className="pack-head">
                    <div className="pack-name">{t(copy.nameKey)}</div>
                    <div className="pack-tag">{t(copy.taglineKey)}</div>
                  </div>

                  <div className="pack-credits">
                    <span className="pack-credits-num">{pack.credits}</span>
                    <span className="pack-credits-label">{t('pricing.creditsLabel')}</span>
                  </div>

                  <div className="pack-price">
                    <span className="pack-currency">$</span>
                    <span className="pack-amount">{formatDollars(pack.priceCents)}</span>
                    <span className="pack-once">{t('pricing.oneOff')}</span>
                  </div>
                  <div className="pack-per">
                    {t('pricing.perCredit', {amount: formatPerCredit(pack.priceCents, pack.credits)})}
                    {pack.savePercent !== null && (
                      <span className="pack-save">{t('pricing.save', {percent: pack.savePercent})}</span>
                    )}
                  </div>

                  <button
                    type="button"
                    className={`pack-cta ${copy.featured ? 'pack-cta-primary' : ''}`}
                    onClick={() => handleBuy(pack.slug)}
                    disabled={ctaDisabled}
                  >
                    {ctaLoading ? t('pricing.redirecting') : t('pricing.buyCredits', {count: pack.credits})}
                  </button>

                  <div className="pack-best">
                    <strong>{t('pricing.bestForLabel')}</strong> {t(copy.bestForKey)}
                  </div>
                </div>
              )
            })}
          </section>
        )}

        {checkoutError && <p className="pack-error">{checkoutError}</p>}

        <section className="included">
          <div className="included-eyebrow">{t('pricing.includedEyebrow')}</div>
          <div className="included-grid">
            {INCLUDED_KEYS.map((key) => (
              <div key={key} className="included-item">
                <span className="included-tick">
                  <Icon name="check" size={14} />
                </span>
                <span>{t(key)}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="credit-explainer">
          <div className="credit-explainer-head">
            <div className="credit-explainer-eyebrow">
              <Icon name="coin" size={16} />
              {t('pricing.howItWorks')}
            </div>
            <h2>
              {t('pricing.howTitlePrefix')} <em>{t('pricing.howTitleEm')}</em>
            </h2>
            <p>{t('pricing.howBody')}</p>
          </div>
          <div className="credit-table">
            <div className="credit-row">
              <div className="credit-row-label">
                <span className="credit-bullet credit-bullet-free">
                  <Icon name="check" size={13} />
                </span>
                {t('pricing.howRows.editor.label')}
              </div>
              <div className="credit-row-meta">{t('pricing.howRows.editor.meta')}</div>
            </div>
            <div className="credit-row">
              <div className="credit-row-label">
                <span className="credit-bullet credit-bullet-paid">
                  <Icon name="spark" size={13} />
                </span>
                {t('pricing.howRows.export.label', {count: packages?.renderCost ?? 0})}
              </div>
              <div className="credit-row-meta">{t('pricing.howRows.export.meta')}</div>
            </div>
            <div className="credit-row">
              <div className="credit-row-label">
                <span className="credit-bullet credit-bullet-free">
                  <Icon name="check" size={13} />
                </span>
                {t('pricing.howRows.redownload.label')}
              </div>
              <div className="credit-row-meta">{t('pricing.howRows.redownload.meta')}</div>
            </div>
            <div className="credit-row">
              <div className="credit-row-label">
                <span className="credit-bullet credit-bullet-bonus">
                  <Icon name="plus" size={13} />
                </span>
                {t('pricing.howRows.fork.label', {count: packages?.forkRewardAmount ?? 0})}
              </div>
              <div className="credit-row-meta">{t('pricing.howRows.fork.meta')}</div>
            </div>
          </div>
        </section>

        <section className="faq">
          <h2 className="faq-title">{t('pricing.faqTitle')}</h2>
          <div className="faq-list">
            {faqItems.map((item, i) => {
              const open = openFaq === i
              return (
                <div key={i} className={`faq-item ${open ? 'open' : ''}`}>
                  <button
                    type="button"
                    className="faq-q"
                    onClick={() => {
                      if (!open) trackEvent('pricing_faq_opened', {faq_index: i, faq_key: FAQ_KEYS[i]})
                      setOpenFaq(open ? -1 : i)
                    }}
                  >
                    <span>{item.q}</span>
                    <span className="faq-q-icon">
                      <Icon name={open ? 'minus' : 'plus'} size={14} />
                    </span>
                  </button>
                  {open && <div className="faq-a">{item.a}</div>}
                </div>
              )
            })}
          </div>
        </section>

        <section className="final-cta">
          <div className="final-cta-card">
            <h2>{t('pricing.cta.title')}</h2>
            <p>{t('pricing.cta.body')}</p>
            <div className="final-cta-btns">
              {user ? (
                <Link to="/editor/new" className="btn-pill btn-pill-primary">
                  {t('pricing.cta.openEditor')}
                  <Icon name="arrow" size={14} />
                </Link>
              ) : (
                <Link to="/login" className="btn-pill btn-pill-primary">
                  {t('pricing.cta.signUp')}
                  <Icon name="arrow" size={14} />
                </Link>
              )}
              <Link to="/projects" className="btn-pill">
                {t('pricing.cta.browse')}
              </Link>
            </div>
          </div>
        </section>
      </main>

      {consentSlug !== null && consentPack !== null && consentCopy !== null && (
        <CheckoutConsentModal
          packageName={t(consentCopy.nameKey)}
          credits={consentPack.credits}
          priceLabel={consentPriceLabel}
          loading={loadingSlug !== null}
          errorKey={consentErrorKey}
          onConfirm={handleConsentConfirm}
          onClose={handleConsentClose}
        />
      )}
    </div>
  )
}

export default PricingPage
